import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, QrCode, ChartLineUp, Gift, Star, Sparkle, Storefront } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Nav */}
      <nav className="border-b border-[var(--border-soft)]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center font-black text-white"
                 style={{ background: "var(--primary)", border: "1.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}>
              RA
            </div>
            <span className="display font-black text-xl">Rating Ally</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login-link" className="text-sm font-semibold hover:underline">
              Sign in
            </Link>
            <Link to="/register" data-testid="landing-cta-register-btn" className="btn-primary" style={{ padding: "8px 18px", fontSize: 14 }}>
              Get started <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-bg opacity-40 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <span className="tag" data-testid="landing-eyebrow">
              <Sparkle size={14} weight="fill" /> Built for restaurants & cafés
            </span>
            <h1 className="display mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
              Turn happy customers into <span style={{ color: "var(--primary)" }}>loyal regulars.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[var(--ink-muted)] max-w-xl">
              Place a QR code on every table. Customers share their feedback and spin a wheel for a chance to win instant rewards — discounts, free items, or surprise prizes you control.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="landing-hero-register-btn" className="btn-primary">
                Start for free <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/login" data-testid="landing-hero-login-btn" className="btn-ghost">
                I already have an account
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm text-[var(--ink-muted)]">
              <div className="flex items-center gap-1.5">
                {[0,1,2,3,4].map(i=>(
                  <Star key={i} size={16} weight="fill" color="#FFCC00" />
                ))}
                <span className="ml-1 font-semibold text-[var(--ink)]">4.9 average rating</span>
              </div>
              <span>•</span>
              <span>No credit card required</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              {/* Floating phone preview */}
              <div className="card-pop p-6 max-w-xs mx-auto animate-float" style={{ background: "var(--secondary)" }}>
                <div className="rounded-2xl bg-white border border-[var(--ink)] p-5 text-center">
                  <div className="text-xs tracking-widest font-bold text-[var(--ink-muted)]">YOU JUST WON</div>
                  <div className="display text-4xl font-black mt-2" style={{ color: "var(--primary)" }}>10% OFF</div>
                  <div className="mt-4 inline-block rounded-lg border border-[var(--ink)] px-3 py-1.5 code-display text-sm">
                    A4F2B1
                  </div>
                  <div className="mt-3 text-xs text-[var(--ink-muted)]">Show this to staff to redeem</div>
                </div>
              </div>
              {/* Floating QR card */}
              <div className="card-pop absolute -left-4 sm:-left-10 -bottom-6 p-3 hidden sm:block" style={{ background: "#fff" }}>
                <div className="h-20 w-20 rounded-md flex items-center justify-center" style={{ background: "var(--ink)" }}>
                  <QrCode size={56} weight="bold" color="#FFFDF5" />
                </div>
              </div>
              <div className="card-pop absolute -right-2 -top-4 p-3 hidden sm:block" style={{ background: "var(--pastel-yellow)" }}>
                <div className="flex items-center gap-2 text-sm font-bold">
                  <Gift size={18} weight="bold" /> New feedback
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: <QrCode size={28} weight="bold" />, title: "Print-ready QR codes", text: "One unique QR per outlet. Download, print, place on tables.", bg: "var(--pastel-blue)" },
            { icon: <Gift size={28} weight="bold" />, title: "Custom spin wheel", text: "Add your own rewards — % off, free items, or 'try again'. You set the odds.", bg: "var(--secondary)" },
            { icon: <ChartLineUp size={28} weight="bold" />, title: "Real analytics", text: "Track scans, spins, wins and redemptions. See conversion rates that matter.", bg: "var(--pastel-purple)" },
          ].map((f, i) => (
            <div key={i} className="card-pop p-7" style={{ background: f.bg }}>
              <div className="h-12 w-12 rounded-xl bg-white border-[1.5px] border-[var(--ink)] flex items-center justify-center mb-5">
                {f.icon}
              </div>
              <h3 className="display text-xl font-extrabold">{f.title}</h3>
              <p className="mt-2 text-[var(--ink-muted)]">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-[var(--border-soft)]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="tag">How it works</span>
            <h2 className="display mt-4 text-3xl sm:text-4xl font-black tracking-tighter">Four steps. Zero friction.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Customer scans", d: "Tap the QR with any phone camera." },
              { n: "02", t: "Shares feedback", d: "Redirected to your preferred feedback page." },
              { n: "03", t: "Spins the wheel", d: "Lands on a reward you configured." },
              { n: "04", t: "Redeems in store", d: "Shows the code, staff marks it used." },
            ].map((s, i) => (
              <div key={i} className="card-soft p-6">
                <div className="display text-sm font-black tracking-widest" style={{ color: "var(--primary)" }}>{s.n}</div>
                <div className="display mt-2 text-lg font-extrabold">{s.t}</div>
                <p className="text-sm text-[var(--ink-muted)] mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="card-pop p-10 sm:p-14 text-center" style={{ background: "var(--primary)", color: "#fff" }}>
          <Storefront size={42} weight="bold" />
          <h2 className="display mt-4 text-3xl sm:text-5xl font-black tracking-tighter">Ready to collect more feedback?</h2>
          <p className="mt-3 text-white/90 max-w-xl mx-auto">Set up your first spinner in under 60 seconds.</p>
          <div className="mt-7 flex justify-center">
            <Link to="/register" data-testid="landing-bottom-cta-btn" className="btn-ghost" style={{ background: "#fff" }}>
              Create my account <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border-soft)] py-8 text-center text-sm text-[var(--ink-muted)]">
        {/* Branding removed */}
      </footer>
    </div>
  );
}
